import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    Button,
    Input,
    Label,
    Switch,
} from 'ui/shadcn';
import type { BoardRow } from './config';

interface BoardFormDialogProps {
    open: boolean;
    mode: 'create' | 'edit';
    initial?: Partial<BoardRow>;
    onClose: () => void;
    onSubmit: (form: { name: string; description: string; visible: boolean }) => Promise<void>;
}

const BoardFormDialog: React.FC<BoardFormDialogProps> = ({
    open,
    mode,
    initial,
    onClose,
    onSubmit,
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [visible, setVisible] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setName(initial?.name || '');
            setDescription(initial?.description || '');
            setVisible(initial?.visible ?? true);
            setSubmitting(false);
        }
    }, [open, initial]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSubmitting(true);
        try {
            await onSubmit({ name: name.trim(), description: description.trim(), visible });
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <DialogHeader>
                        <DialogTitle>
                            {mode === 'create' ? '게시판 생성' : '게시판 수정'}
                        </DialogTitle>
                        <DialogDescription>
                            게시판의 이름과 공개 여부를 입력하세요.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="board-name">
                                이름 <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="board-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="예: 공지사항"
                                required
                                autoFocus
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="board-desc">설명</Label>
                            <Input
                                id="board-desc"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="게시판에 대한 간단한 설명"
                            />
                        </div>
                        <div className="flex items-center justify-between rounded border border-border px-3 py-2">
                            <div className="flex flex-col">
                                <Label htmlFor="board-visible" className="text-base font-medium">
                                    공개 여부
                                </Label>
                                <span className="text-xs text-muted-foreground">
                                    비공개 시 일반 직원에게 노출되지 않습니다.
                                </span>
                            </div>
                            <Switch
                                id="board-visible"
                                checked={visible}
                                onCheckedChange={setVisible}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            취소
                        </Button>
                        <Button type="submit" disabled={submitting || !name.trim()}>
                            {submitting ? '저장 중...' : mode === 'create' ? '생성' : '저장'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default BoardFormDialog;
